import { useQuery } from '@tanstack/react-query';
import { api, type Org, type Space, type Team } from './api';
import type { TrackerProject } from '../pages/tracker/model';

/**
 * Spaces (crates/server/src/api/spaces.rs) and the navigation tree built from them: the same
 * sidebar for boards and tasks, scaled to who uses it. One person with one space sees a single
 * row; a company with many teams sees teams, each with its spaces; tracker projects sit inside
 * their spaces.
 */

export const spacesKey = (org: string | null) => ['spaces', org ?? 'personal'] as const;

/** Spaces of the person's teams in the organization (`null`: the personal context). */
export function useSpaces(org: string | null) {
  return useQuery({ queryKey: spacesKey(org), queryFn: () => api.get<Space[]>(`/spaces?org=${org ?? 'personal'}`), staleTime: 30_000 });
}

/** Spaces of one team (moving boards, choosing where a new thing goes). */
export function useTeamSpaces(team: string | null | undefined) {
  return useQuery({ queryKey: ['spaces', 'team', team], queryFn: () => api.get<Space[]>(`/spaces?team=${team}`), enabled: !!team, staleTime: 30_000 });
}

export function useSpace(id: string | null | undefined) {
  return useQuery({ queryKey: ['spaces', 'one', id], queryFn: () => api.get<Space>(`/spaces/${id}`), enabled: !!id });
}

// ---------------------------------------------------------------------------------------------
// Which organization the sidebar shows

const ORG_KEY = 'vatman.org';

/** Remembers the organization last looked at (`null`: personal), for pages without `?org=`. */
export function rememberOrg(org: string | null) {
  try {
    localStorage.setItem(ORG_KEY, org ?? 'personal');
  } catch {
    // Private mode: the sidebar then starts from the personal context.
  }
}

function rememberedOrg(): string | null | undefined {
  try {
    const v = localStorage.getItem(ORG_KEY);
    if (v === null) return undefined;
    return v === 'personal' ? null : v;
  } catch {
    return undefined;
  }
}

/**
 * The organization in view: what the page is about (`hint`: the org of an open project or
 * space, `null` for personal), else the remembered one, if the person is still in it.
 */
export function orgContext(orgs: Org[], hint?: string | null): string | null {
  const known = (id: string | null | undefined) => id === null || (id !== undefined && orgs.some((o) => o.id === id));
  if (hint !== undefined && known(hint)) return hint;
  const stored = rememberedOrg();
  return known(stored) ? (stored ?? null) : null;
}

// ---------------------------------------------------------------------------------------------
// The tree

export interface NavSpace {
  space: Space;
  projects: TrackerProject[];
}

export interface NavGroup {
  team: Team;
  /** The team has just its default space: one row for the team, no level in between. */
  flat: boolean;
  spaces: NavSpace[];
}

export interface NavTree {
  /** More than one team here: spaces are grouped under their teams. */
  showTeams: boolean;
  groups: NavGroup[];
  /** Projects reachable here but not in the person's own spaces: by organization access, or as a guest. */
  others: TrackerProject[];
}

const bySpaceOrder = (a: Space, b: Space) => Number(b.isDefault) - Number(a.isDefault) || a.position - b.position || a.name.localeCompare(b.name);

export function buildNavTree(org: string | null, teams: Team[], orgs: Org[], spaces: Space[], projects: TrackerProject[]): NavTree {
  const mine = teams.filter((t) => (org ? t.orgId === org : t.orgId === null));
  // Spaces of other teams one was given a role in get their team's group too.
  const known = new Set(mine.map((t) => t.id));
  const visiting: Team[] = [];
  for (const s of spaces) {
    if (known.has(s.teamId) || (org ? s.orgId !== org : s.orgId !== null)) continue;
    known.add(s.teamId);
    visiting.push({ id: s.teamId, orgId: s.orgId, name: s.teamName, personal: false, role: 'member', members: 0 });
  }
  const here = [...mine, ...visiting].sort((a, b) => Number(b.personal) - Number(a.personal) || a.name.localeCompare(b.name));
  const placed = new Set<string>();
  const groups: NavGroup[] = here.map((team) => {
    const own = spaces.filter((s) => s.teamId === team.id).sort(bySpaceOrder);
    // Projects of other teams this team works in (docs/teams-plan.md) sit in its default space.
    const sharedIn = projects.filter((p) => p.teams?.some((tm) => tm.id === team.id && !tm.home));
    const list = own.map((space, i) => {
      const inside = projects.filter((p) => p.spaceId === space.id || (i === 0 && sharedIn.includes(p))).sort((a, b) => a.key.localeCompare(b.key));
      inside.forEach((p) => placed.add(p.id));
      return { space, projects: inside };
    });
    return { team, flat: list.length === 1 && list[0].space.isDefault, spaces: list };
  });
  const memberOf = new Set(orgs.map((o) => o.id));
  // Whatever is reachable outside one's spaces: by a role in the project, the organization's
  // access or as a guest.
  const others = projects
    .filter((p) => !placed.has(p.id))
    // Projects of organizations the person is not in (a guest) show in the personal context.
    .filter((p) => (org ? p.orgId === org : p.orgId === null || !memberOf.has(p.orgId)))
    .sort((a, b) => a.key.localeCompare(b.key));
  return { showTeams: here.length > 1, groups, others };
}
